import { cn } from '@sandtech-hub-monorepo/ui-kit';
import {
  BadgeStatus,
  IssuedCourseBadgeFragment,
} from '@sandtech-hub-monorepo/ehub-client';
import { formatDate } from '@sandtech-hub-monorepo/ui-kit-site';

import { BadgeActionsOverlay } from './BadgeActionsOverlay';
import { RevocationTooltip } from './RevocationTooltip';

interface Props {
  badge: IssuedCourseBadgeFragment;
  courseName: string;
  /** 1-based position in the program, for "Course 2: …". */
  position: number;
}

export const CourseBadgeCard = ({ badge, courseName, position }: Props) => {
  const revoked = badge.status === BadgeStatus.Revoked;
  const imageUrl = revoked
    ? badge.badgeDefinition?.revokedImageUrl ?? badge.badgeDefinition?.imageUrl
    : badge.badgeDefinition?.imageUrl;
  const date = revoked ? badge.revokedAt : badge.issuedAt;

  const card = (
    <div
      className={cn(
        'group relative flex w-full flex-col items-center gap-1.5 overflow-hidden rounded-lg border border-border-secondary p-3 text-center',
        revoked ? 'bg-destructive/5' : 'bg-card'
      )}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={`${courseName} badge`}
          className="h-14 w-auto"
        />
      )}
      <span
        className={cn(
          'rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium',
          revoked
            ? 'bg-destructive/15 text-destructive'
            : 'bg-success/15 text-success'
        )}
      >
        {revoked ? 'Revoked' : 'Active'}
      </span>
      <span className="text-sm text-heading">{courseName}</span>
      {date && (
        <span className="text-[11px] text-muted-foreground">
          {revoked ? 'Revoked' : 'Earned'} on {formatDate(date)}
        </span>
      )}

      {!revoked && (
        <BadgeActionsOverlay
          imageUrl={imageUrl}
          fileName={`${courseName} badge.png`}
          share={{
            title: `Course ${position}: ${courseName}`,
            typeLabel: 'Course',
            imageUrl,
            earnedAt: badge.issuedAt,
            shareUrl: badge.linkedInShareUrl,
          }}
        />
      )}
    </div>
  );

  return revoked ? (
    <RevocationTooltip reason={badge.revocationReason}>
      {card}
    </RevocationTooltip>
  ) : (
    card
  );
};
